import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Scale } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtDate, todayISO } from "@/lib/api";
import { Field, EmptyState } from "@/components/Field";
import { ProgressChart, METRICS } from "@/components/ProgressChart";

const blank = () => ({ date: todayISO(), notes: "", measure_notes: {} });
const num = (v) => (v === "" || v == null ? null : Number(v));
const VISIT_FIELDS = [{ key: "height", label: "Altezza", unit: "cm" }, ...METRICS];

const VisitDialog = ({ open, onOpenChange, pid, onSaved }) => {
  const [f, setF] = useState(blank());
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const measure_notes = Object.fromEntries(Object.entries(f.measure_notes).filter(([, v]) => v.trim()));
      const payload = { date: f.date, notes: f.notes, measure_notes };
      VISIT_FIELDS.forEach((m) => { payload[m.key] = num(f[m.key]); });
      const { data } = await api.post(`/patients/${pid}/visits`, payload);
      toast.success("Visita registrata", { description: data.email_sent ? "Il paziente è stato avvisato via email" : "Notifica inviata nell'area personale" });
      setF(blank());
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto rounded-2xl" data-testid="visit-dialog">
        <DialogHeader><DialogTitle>Nuova visita</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Data visita"><input type="date" required className="field" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="visit-date-input" /></Field>
          <div className="space-y-2">
            {VISIT_FIELDS.map((m) => (
              <div key={m.key} className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] items-end gap-3">
                <Field label={`${m.label} (${m.unit})`}>
                  <input type="number" step="0.1" className="field" value={f[m.key] ?? ""} onChange={(e) => setF({ ...f, [m.key]: e.target.value })} data-testid={`visit-${m.key}-input`} />
                </Field>
                <input className="field" placeholder="Note" value={f.measure_notes[m.key] || ""} onChange={(e) => setF({ ...f, measure_notes: { ...f.measure_notes, [m.key]: e.target.value } })} data-testid={`visit-${m.key}-note-input`} />
              </div>
            ))}
          </div>
          <Field label="Note della visita (solo dottoressa)">
            <textarea className="field min-h-[70px]" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="visit-notes-input" />
          </Field>
          <div className="flex justify-end"><button className="btn-primary" disabled={saving} data-testid="visit-save-button">Salva visita</button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export function VisitsTab({ rec, reload }) {
  const [open, setOpen] = useState(false);
  const visits = rec.visits;
  const remove = async (id) => {
    if (!window.confirm("Eliminare questa visita?")) return;
    await api.delete(`/visits/${id}`);
    reload();
  };
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">Andamento</h3>
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="add-visit-button"><Plus className="h-4 w-4" /> Nuova visita</button>
      </div>
      {visits.length === 0 ? (
        <EmptyState icon={Scale} title="Nessuna visita registrata" text="Registra peso e circonferenze ad ogni visita per seguire i progressi." testid="visits-empty" />
      ) : (
        <>
          <ProgressChart visits={visits} prefix="doctor-progress" />
          <div className="overflow-x-auto rounded-2xl border border-[#e2e8f0] bg-white">
            <table className="w-full min-w-[720px] text-left text-sm" data-testid="visits-table">
              <thead className="bg-[#f8fafc]"><tr>
                <th className="label-xs px-4 py-3">Data</th>
                {VISIT_FIELDS.map((m) => <th key={m.key} className="label-xs px-4 py-3">{m.label}</th>)}
                <th className="label-xs px-4 py-3">Note</th><th />
              </tr></thead>
              <tbody>
                {[...visits].reverse().map((v) => (
                  <tr key={v.id} className="border-t border-[#e2e8f0]" data-testid={`visit-row-${v.id}`}>
                    <td className="px-4 py-3 font-semibold">{fmtDate(v.date)}</td>
                    {VISIT_FIELDS.map((m) => (
                      <td key={m.key} className="px-4 py-3 align-top">
                        {v[m.key] ?? "—"}
                        {v.measure_notes?.[m.key] && <div className="mt-0.5 max-w-[140px] text-xs text-slate-500" data-testid={`visit-${v.id}-${m.key}-note`}>{v.measure_notes[m.key]}</div>}
                      </td>
                    ))}
                    <td className="max-w-[220px] truncate px-4 py-3 text-slate-500">{v.notes}</td>
                    <td className="px-2"><button className="btn-ghost text-slate-400 hover:text-rose-600" onClick={() => remove(v.id)} data-testid={`visit-delete-${v.id}`}><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <VisitDialog open={open} onOpenChange={setOpen} pid={rec.patient.id} onSaved={reload} />
    </div>
  );
}
